import { getAvailableProductLinks, getCustomLinkItems, type SwitcherItemType } from './links';
import { hasLoaded, isComplete, isError, Status } from '../providers/as-data-provider';
import {
	type CustomizeLinks,
	type FeatureMap,
	type GetExtendedAnalyticsAttributes,
	type MapUrl,
	type Organisation,
	Product,
	type ProviderResults,
	type SyntheticProviderResults,
} from '../../types';
import { createCollector } from './create-collector';
import { collectAdminLinks } from '../../admin/utils/admin-link-collector';
import { collectJoinableSiteLinks } from '../../cross-join/utils/cross-join-link-collector';
import { UserSiteDataError, UserSiteDataErrorReason } from './errors/user-site-data-error';
import { SwitcherLoadError, SwitcherLoadErrorReason } from './errors/switcher-load-error';
import { buildAtlassianHomeOrStartSwitcherItemType } from './atlassian-home-or-start-product';
import { buildRovoScasProducts } from './build-rovo-scas-products';
import { buildCompanyHub } from './build-company-hub';
import { buildTeamsProduct } from './build-teams-product';
import { buildGoalsProduct } from './build-goals-product';
import { formatLabel } from '../providers/product-configuration-provider';
import messages from './messages';
import { type Viewer } from './nudge-to-jira';

function collectAvailableProductLinks(
	cloudId: string | null | undefined,
	mapUrl: MapUrl,
	productConfiguration?: ProviderResults['productConfiguration'],
	availableProducts?: ProviderResults['availableProducts'],
	orgId?: string,
	viewer?: Viewer,
): SwitcherItemType[] | undefined {
	if (availableProducts && productConfiguration) {
		if (isError(productConfiguration)) {
			throw new SwitcherLoadError(
				SwitcherLoadErrorReason.PRODUCT_CONFIGURATION,
				productConfiguration.error,
			);
		}

		if (isError(availableProducts)) {
			throw new SwitcherLoadError(
				SwitcherLoadErrorReason.AVAILABLE_PRODUCTS,
				availableProducts.error,
			);
		}

		if (isComplete(productConfiguration) && isComplete(availableProducts)) {
			let productConfigurationMap = productConfiguration.data.products;

			// all T25 features need to put behind "test" experiment group
			if (availableProducts.data.switcherTeam25ExperimentCohort === 'test') {
				// add Goals config
				productConfigurationMap = buildGoalsProduct(
					productConfigurationMap,
					availableProducts.data?.isGoalsAsAppEnabled,
				);

				// add Teams config
				productConfigurationMap = buildTeamsProduct(
					productConfigurationMap,
					availableProducts.data?.isTeamsAsAppEnabled,
				);

				// add SCAS config
				productConfigurationMap = buildRovoScasProducts(
					productConfigurationMap,
					availableProducts.data?.isScasAsAppEnabled,
				);
			}

			if (availableProducts.data.isAnalyticsRebrandEnabled && productConfigurationMap.ANALYTICS) {
				productConfigurationMap.ANALYTICS.iconKey = 'atlassian-analytics-rebrand';
			}

			// analytics i18n
			if (productConfigurationMap.ANALYTICS) {
				productConfigurationMap.ANALYTICS.label = formatLabel(messages.analytics);
			}

			return getAvailableProductLinks(
				productConfigurationMap,
				availableProducts.data,
				cloudId,
				mapUrl,
				orgId,
				viewer,
			);
		}
		return;
	}
	return;
}

function collectIsPartialResult(availableProducts?: ProviderResults['availableProducts']) {
	if (availableProducts) {
		if (isError(availableProducts)) {
			throw new SwitcherLoadError(
				SwitcherLoadErrorReason.AVAILABLE_PRODUCTS,
				availableProducts.error,
			);
		}

		if (isComplete(availableProducts)) {
			return availableProducts.data.isPartial;
		}
		return false;
	}
	return false;
}

function collectCanManageLinks(managePermission: ProviderResults['managePermission']) {
	if (isComplete(managePermission)) {
		return managePermission.data;
	}
}

function extractExploreProductsLink(productConfiguration: ProviderResults['productConfiguration']) {
	const EXPLORE_PRODUCTS_LINK_TYPE = 'ATLASSIAN_WEBSITE_TRY_PRODUCTS';
	const exploreProductsFallbackLink = 'https://www.atlassian.com/try';

	if (isComplete(productConfiguration)) {
		return (
			productConfiguration.data.links[EXPLORE_PRODUCTS_LINK_TYPE]?.href ||
			exploreProductsFallbackLink
		);
	}

	return exploreProductsFallbackLink;
}

function collectCustomLinks(
	customLinks: ProviderResults['customLinks'],
	userSiteData: SyntheticProviderResults['userSiteData'],
) {
	if (customLinks === undefined || isError(userSiteData)) {
		return [];
	}

	if (isComplete(customLinks) && isComplete(userSiteData)) {
		return getCustomLinkItems(customLinks.data, userSiteData.data.currentSite);
	}
}

function asUserSiteDataProviderResult(
	availableProductsProvider: ProviderResults['availableProducts'],
	cloudId: string | null | undefined,
	product: Product | null | undefined,
): SyntheticProviderResults['userSiteData'] {
	switch (availableProductsProvider.status) {
		case Status.LOADING: // intentional fallthrough
		case Status.ERROR:
			return availableProductsProvider;
		case Status.COMPLETE:
			if (availableProductsProvider.data.sites.length === 0) {
				const reason = availableProductsProvider.data.isPartial
					? UserSiteDataErrorReason.APS_PARTIAL_EMPTY_RESULT
					: UserSiteDataErrorReason.APS_EMPTY_RESULT;
				return {
					status: Status.ERROR,
					data: null,
					error: new UserSiteDataError(reason, `Available products returned an empty list`),
				};
			}
			const site = availableProductsProvider.data.sites.find(
				(site) =>
					(cloudId && site.cloudId && site.cloudId?.toLowerCase() === cloudId.toLowerCase()) ||
					// below will capture tenantless products as we set the cloudId to the productId in APS
					(product && site.cloudId && site.cloudId?.toLowerCase() === product.toLowerCase()),
			);

			if (!site) {
				return {
					status: Status.ERROR,
					data: null,
					error: new UserSiteDataError(
						UserSiteDataErrorReason.APS_NO_SITE_MATCH,
						`could not find site in availableProducts for cloudId ${cloudId}`,
					),
				};
			}
			return {
				status: Status.COMPLETE,
				data: {
					currentSite: {
						url: site.url,
						products: site.availableProducts,
					},
				},
			};
	}
}

type MapResultsToSwitcherPropsParams = {
	cloudId: string | null | undefined;
	results: ProviderResults;
	features: FeatureMap;
	product?: Product;
	adminUrl?: string;
	customizeLinks?: CustomizeLinks;
	orgId: string;
	viewer?: Viewer;
};

export function mapResultsToSwitcherProps({
	cloudId,
	results,
	features,
	product,
	adminUrl,
	customizeLinks,
	orgId,
	viewer,
}: MapResultsToSwitcherPropsParams) {
	const collect = createCollector();

	const { availableProducts, productConfiguration, joinableSites, managePermission, customLinks } =
		results;

	const userSiteData = asUserSiteDataProviderResult(availableProducts, cloudId, product);
	const hasLoadedAvailableProducts =
		hasLoaded(productConfiguration) && hasLoaded(availableProducts);
	const hasLoadedAdminLinks = hasLoaded(managePermission);

	const hasLoadedJoinableSites = hasLoaded(joinableSites);

	let mapUrl: MapUrl = (url) => url;
	let getExtendedAnalyticsAttributes: GetExtendedAnalyticsAttributes = () => ({});
	if (customizeLinks) {
		const originCustomLinks = customizeLinks();
		mapUrl = originCustomLinks.mapUrl;
		getExtendedAnalyticsAttributes = originCustomLinks.getExtendedAnalyticsAttributes;
	}

	const organisations = availableProducts.data?.sites.reduce<Map<Organisation['id'], Organisation>>(
		(acc, site) => {
			if (site.org && !acc.has(site.org.id)) {
				acc.set(site.org.id, site.org);
			}

			return acc;
		},
		new Map<Organisation['id'], Organisation>(),
	);

	const licensedProductLinks = collect(
		collectAvailableProductLinks(
			cloudId,
			mapUrl,
			productConfiguration,
			availableProducts,
			orgId,
			viewer,
		),
		[],
	);

	if (hasLoaded(availableProducts) && licensedProductLinks.length > 0) {
		// 1. Add Company hub
		const item = buildCompanyHub(availableProducts, productConfiguration, cloudId);
		if (item) {
			licensedProductLinks.unshift(item);
		}

		// 2. Add Atlassian Home or Start
		if (product !== Product.START) {
			licensedProductLinks.unshift(
				buildAtlassianHomeOrStartSwitcherItemType(orgId, availableProducts),
			);
		}
	}

	return {
		organisations,
		getExtendedAnalyticsAttributes,
		licensedProductLinks,
		isPartialResult: collectIsPartialResult(availableProducts),
		adminLinks: collect(collectAdminLinks(availableProducts, productConfiguration, adminUrl), []),
		joinableSiteLinks: collect(collectJoinableSiteLinks(joinableSites, productConfiguration), []),
		customLinks: collect(collectCustomLinks(customLinks, userSiteData), []),
		exploreProductsLink: extractExploreProductsLink(productConfiguration),
		showManageLink: collect(collectCanManageLinks(managePermission), false),
		hasLoaded: hasLoadedAvailableProducts && hasLoadedAdminLinks && hasLoadedJoinableSites,
		hasLoadedCritical: hasLoadedAvailableProducts,
		hasLoadedInstanceProviders: customLinks && hasLoaded(customLinks) && hasLoaded(userSiteData),
		rawProviderResults: {
			productConfiguration,
			availableProducts,
			joinableSites,
			managePermission,
			customLinks,
			userSiteData,
		},
		features,
		switcherTeam25ExperimentCohort: availableProducts?.data?.switcherTeam25ExperimentCohort,
		isTeam25AppRebrandEnabled: availableProducts?.data?.isTeam25AppRebrandEnabled,
	};
}
